import { createSelector } from "reselect";
import {
  DISPLAY_DATE_FORMAT,
  newTeamName,
  OrgStatus,
} from "../../utils/constants";
import {
  capitalize,
  formatDate,
  Owner,
  pendingActionsTypes,
} from "../../utils/utils";
import { RootState } from "../store";
import { selectOrgSubscriptionsSummaries } from "../subscription/subscription.selector";
import { OrgState } from "./org.reducer";
import { OrgSubscription } from "./org.types";
import { api } from "../api/api";
import dayjs from "dayjs";

export const selectOrgReducer = (state: RootState): OrgState => state.org;

const selectOrgs = (state: RootState) =>
  api.endpoints.getOrgs.select()(state).data || [];

export const selectOrgsWithStatus = createSelector(
  [selectOrgs, selectOrgSubscriptionsSummaries],
  (orgs, summaries) => {
    return (
      orgs?.map((org) => {
        const summary = summaries.find((s) => s.org_id === org.id);
        const {
          is_active: isActive,
          is_expiring_soon: isExpiringSoon,
          is_trial: isTrial,
          is_trial_active: isTrialActive,
        } = summary || {};

        return {
          ...org,
          status: isTrial
            ? isTrialActive
              ? OrgStatus.Trial
              : OrgStatus.TrialExpired
            : isActive
            ? OrgStatus.Active
            : isExpiringSoon
            ? OrgStatus.ExpiringSoon
            : OrgStatus.Expired,
          is_trial: summary?.is_trial,
          is_trial_active: summary?.is_trial_active,
          is_active: summary?.is_active,
          subscription: summary?.subscription,
          crm: {
            paid: summary?.is_paid_crm_active,
            trial: summary?.is_trial_crm_active,
          },
        };
      }) || []
    );
  }
);

export const selectUserSyncData = (state: RootState) =>
  api.endpoints.usersSync.select()(state).data;

export const selectUser = createSelector(
  [selectUserSyncData],
  (user) => user?.user
);

export const selectIsOnlyOneActiveOrg = createSelector(
  [selectUser, selectOrgsWithStatus],
  (user, orgs) => {
    if (user?.role_name === Owner) {
      if (orgs?.length === 1) {
        return orgs[0].is_active === true;
      } else if (orgs?.length === 2) {
        const activeOrg = orgs.find((org) => org.is_active === true);
        const deleteableOrg = orgs.find((org) => isDeletableOrg(org));
        return !!activeOrg && !!deleteableOrg;
      }
    }

    return false;
  }
);

export const selectTopStripData = createSelector(
  [selectOrgsWithStatus],
  (orgs) => {
    const expired = orgs.find((org) => org.status === OrgStatus.Expired);
    const trialExpired = orgs.find(
      (org) => org.status === OrgStatus.TrialExpired
    );
    const crmTrial = orgs.find((o) => !!o.crm.trial && !o.crm.paid);
    const expiringSoon = orgs.find(
      (org) => org.status === OrgStatus.ExpiringSoon
    );

    if (expired) {
      return {
        org: expired,
        code: "Expired",
        bgColor: "rgba(229, 26, 35, 0.57)",
        text: "One or more of your Superfone subscriptions have expired. Click here to renew",
        strong: "expired. Click here to renew",
      };
    } else if (trialExpired) {
      return {
        org: trialExpired,
        code: "Trial Expired",
        bgColor: "rgba(229, 26, 35, 0.57)",
        text: "Your Superfone trial(s) have expired. Click here to start your subscription",
        strong: "expired. Click here to start your subscription",
      };
    } else if (crmTrial) {
      return {
        org: crmTrial,
        code: "CRM Trial",
        bgColor: "rgba(237, 211, 209, 1)",
        text: `CRM Trial Enabled for your Team ${
          crmTrial?.name || ""
        }. Trial period ends on ${
          crmTrial?.crm_trial?.valid_till
            ? dayjs(crmTrial.crm_trial.valid_till).format(DISPLAY_DATE_FORMAT)
            : ""
        }. `,
        strong: "Book Setup",
      };
    } else if (expiringSoon) {
      return {
        org: expiringSoon,
        code: "Expiring Soon",
        bgColor: "rgba(253, 253, 150, 1)",
        text: "One or more of your Superfone subscriptions are expiring soon. Click here to renew",
        strong: "expiring soon. Click here to renew",
      };
    } else
      return { org: null, code: "Active", bgColor: "rgba(22, 157, 0, 0.57)" };
  }
);

export const selectOrgWithStatusRows = createSelector(
  [selectOrgsWithStatus],
  (orgsWithStatus) => {
    return transformToTableData(orgsWithStatus);
  }
);

type OrgsWithStatus = ReturnType<typeof selectOrgsWithStatus>;

export type OrgWithStatusRow = ReturnType<
  typeof selectOrgWithStatusRows
>[number];

export const selectOrgsCount = createSelector(
  [selectOrgs],
  (orgs) => orgs.length || 0
);

export const selectOrgSubscriptionSummary = (state: RootState) =>
  api.endpoints.getOrgSubscriptionSummary.select(state.org.selectedOrgId)(state)
    .data;

export const selectOrgStatus = createSelector(
  [selectOrgSubscriptionSummary],
  (orgSubscriptionSummary) => {
    const {
      is_active: isActive,
      is_expiring_soon: isExpiringSoon,
      is_trial: isTrial,
      is_trial_active: isTrialActive,
    } = orgSubscriptionSummary || {};
    return isTrial
      ? isTrialActive
        ? OrgStatus.Trial
        : OrgStatus.TrialExpired
      : isActive
      ? OrgStatus.Active
      : isExpiringSoon
      ? OrgStatus.ExpiringSoon
      : OrgStatus.Expired;
  }
);

export const selectOrgStatuses = createSelector(
  [selectOrgSubscriptionSummary],
  (summary) => {
    return {
      is_active: summary?.is_active,
      is_trial_active: summary?.is_trial_active,
      is_trial: summary?.is_trial,
      subscription: summary?.subscription,
    };
  }
);

export const selectOrgTrial = createSelector(
  [selectOrgSubscriptionSummary],
  (orgSubscriptionSummary) => {
    return orgSubscriptionSummary?.is_trial;
  }
);

export const selectIsOrgActive = createSelector(
  [selectOrgStatus],
  (orgStatus) =>
    orgStatus === OrgStatus.Active || orgStatus === OrgStatus.ExpiringSoon
);

export const selectOrgSubscriptions = (state: RootState) =>
  api.endpoints.getOrgSubscriptions.select(state.org.selectedOrgId)(state)
    .data || [];

export const selectOrgSubscriptionRows = createSelector(
  [selectOrgSubscriptions],
  (orgSubscriptions) => {
    return transformToSubscriptionTableRows(orgSubscriptions);
  }
);

export type OrgSubscriptionRow = ReturnType<
  typeof selectOrgSubscriptionRows
>[number];

export const selectOrgSubscriptionsSummary = (state: RootState) =>
  api.endpoints.getOrgSubscriptionSummary.select(state.org.selectedOrgId)(state)
    .data;

export const selectSubscription = createSelector(
  [selectOrgSubscriptionsSummary],
  (orgSubscriptionSummary) => orgSubscriptionSummary?.subscription
);

export const selectOrgExpiryDate = createSelector(
  [selectSubscription],
  (subscription) => formatDate(subscription?.valid_till)
);

const transformToTableData = (data: OrgsWithStatus) => {
  return data.map((data, index) => ({
    snno: index + 1,
    id: data.id,
    teamId: data.id,
    teamName: data.name,
    superfoneNumber: data.superfone_number,
    status: data.status,
    teamStaffsCount: data.users.length || 0,
    isTrial: data.is_trial,
    is_active: data.is_active,
    is_trial_active: data.is_trial_active,
    subscription: data.subscription,
    is_trial: data.is_trial,
  }));
};

const transformToSubscriptionTableRows = (data: OrgSubscription[]) => {
  return data.map((o, index) => ({
    snno: index + 1,
    id: o.id,
    subscriptionId: o.id,
    title: o.title,
    status: capitalize(o.status),
    startedOn: formatDate(o.createdAt),
    validTill: formatDate(o.valid_till),
    payment: o.payment,
  }));
};

export type Statuses = Required<
  Pick<any, "is_active" | "is_trial" | "is_trial_active" | "subscription">
>;

export const isDeletableOrg = (statuses: Statuses) => {
  return (
    statuses?.is_trial === false &&
    statuses?.is_active === false &&
    statuses?.is_trial_active === false &&
    !statuses?.subscription
  );
};

export const selectNewOrgs = createSelector([selectOrgsWithStatus], (orgs) => {
  const mainOrgId = Math.min(...orgs.map((org) => org.id));
  const newOrgsWith2PendingActions = orgs
    .filter(
      (org) =>
        org.name === newTeamName &&
        org.users.length <= 1 &&
        org.is_active === true
    )
    .map((org) => ({
      ...org,
      actions: [
        pendingActionsTypes.changeTeamName,
        pendingActionsTypes.addStaff,
      ],
    }));
  const newOrgsWithSystemName = orgs
    .filter((org) => {
      const alreadyExists = newOrgsWith2PendingActions.find(
        (newOrg) => newOrg.id === org.id
      );
      if (alreadyExists) {
        return false;
      }
      return true;
    })
    .filter((org) => org.name === newTeamName && org.is_active === true)
    .map((org) => ({
      ...org,
      actions: [pendingActionsTypes.changeTeamName],
    }));

  const newOrgsWith1Staff = orgs
    .filter((org) => {
      const alreadyExists = newOrgsWith2PendingActions.find(
        (newOrg) => newOrg.id === org.id
      );
      if (alreadyExists) {
        return false;
      }
      return true;
    })
    .filter(
      (org) =>
        org.users.length <= 1 && org.is_active === true && org.id !== mainOrgId
    )
    .map((org) => ({
      ...org,
      actions: [pendingActionsTypes.addStaff],
    }));

  return [
    ...newOrgsWith2PendingActions,
    ...newOrgsWithSystemName,
    ...newOrgsWith1Staff,
  ];
});

export const showPendingActionAlert = createSelector(
  [selectNewOrgs],
  (orgs) => orgs.length > 0
);

export const selectEditMode = createSelector(
  [selectOrgReducer],
  (org) => org.editMode
);

export const selectGetAllOrgsSettings = (state: RootState) => {
  return api.endpoints.getAllOrgsSettings.select()(state)?.data || [];
};

export const selectAllOrgsWithCallerTune = createSelector(
  [selectGetAllOrgsSettings, selectOrgs],
  (orgSettings, orgs) => {
    const filteredOrgs =
      orgSettings?.filter(
        (settings) => settings?.[0]?.caller_tune === "custom"
      ) || [];
    return filteredOrgs.map((settings) => {
      const thisOrg = orgs.find((o) => o.id === settings?.[0]?.org_id);
      return {
        orgDetails: thisOrg,
        settings: settings,
      };
    });
  }
);

// export const selectFBPages = (state: RootState) =>
//   api.endpoints?.getFaceBookPages.select(state.org.selectedOrgId)(state)
//     .data || [];
// export const selectFBPagesSelector = createSelector(
//   [selectFBPages],
//   (pages) => {
//     return pages.map((o: any, index: number) => ({
//       snno: index + 1,
//       id: o.id,
//       // subscriptionId: o.id,
//       // title: o.title,
//       // status: capitalize(o.status),
//       // startedOn: formatDate(o.createdAt),
//       // validTill: formatDate(o.valid_till),
//       // payment: o.payment,
//     }));
//   }
// );
